/** You › Notifications: push, categories, quiet hours (digests / email land here). */
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@viko/transcript-ui'
import { DigestSettingsPanel } from '../../components/DigestSettings.js'
import { LatencyStats } from '../../components/LatencyStats.js' // g12-overview
import { errorText, useToast } from '../../components/ui.js'
import { useBackend } from '../../lib/backend.js'
import { GATE_COPY, pushGateNow } from '../../lib/ios-pwa.js'
import { disablePush, enablePush } from '../../lib/push.js'
import { qk, useSettings } from '../../lib/queries.js'
import type { Settings } from '../../lib/types.js'
import { Section } from './shared.js'

export function NotificationsSection() {
  return (
    <>
      <Notifications />
      {/* ---- g13-digests: digest times, scope, push and opt-in email ---- */}
      <Section id="digests" title="Digests">
        <DigestSettingsPanel />
      </Section>
      {/* ---- end g13-digests ---- */}
      {/* ---- g12-overview: blocked→known p50/p95, drillable by stage ---- */}
      <Section id="response-time" title="Response time">
        <LatencyStats />
      </Section>
      {/* ---- end g12-overview ---- */}
    </>
  )
}

function Notifications() {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const settings = useSettings()
  const s = settings.data
  if (!s) return null
  const save = async (next: Settings) => {
    qc.setQueryData(qk.settings, next)
    try {
      await b.api.saveSettings(next)
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  const togglePush = async () => {
    if (s.push) {
      await disablePush(b.api)
      return save({ ...s, push: false })
    }
    // The permission prompt must come straight from this tap (iOS ignores it otherwise).
    const r = await enablePush(b.api, b.deviceKeyId)
    if (r === 'enabled') return save({ ...s, push: true })
    toast(
      r === 'denied'
        ? 'Notifications are blocked for Viko in this browser’s settings.'
        : r === 'unsupported'
          ? 'Add Viko to your Home Screen to get notifications on iPhone.'
          : 'Push is not configured on the server yet.',
      'error',
    )
  }
  const gate = pushGateNow()
  const cat = (k: keyof Settings['categories'], label: string) => (
    <label key={k} className="row !cursor-pointer items-center !border-0 !py-2">
      <span className="flex-1 text-[15px]">{label}</span>
      <input
        type="checkbox"
        className="h-5 w-5 accent-[var(--vk-accent)]"
        checked={s.categories[k]}
        onChange={(e) =>
          void save({ ...s, categories: { ...s.categories, [k]: e.target.checked } })
        }
      />
    </label>
  )
  return (
    <Section id="notifications" title="Notifications">
      <div className="px-4 pb-2">
        <Button
          variant={s.push ? 'secondary' : 'primary'}
          size="sm"
          disabled={gate !== 'ready' && !s.push}
          onClick={() => void togglePush()}
        >
          {s.push ? 'Turn off push on this device' : 'Turn on push for this device'}
        </Button>
        {gate !== 'ready' && !s.push ? (
          <p role="note" data-push-gate={gate} className="m-0 mt-2 text-[14px] text-text-2">
            {GATE_COPY[gate]}
          </p>
        ) : null}
        <p className="m-0 mt-2 text-[14px] text-text-3">
          Only blocking items push right away, grouped per workstream. Repeats in the same
          workstream within 20 seconds join one notification.
        </p>
      </div>
      {cat('questions', 'Questions from agents')}
      {cat('approvals', 'Approvals')}
      {cat('landing', 'Landing results and failures')}
      {cat('builds', 'New builds')}
      {cat('machines', 'Machine offline or blocked')}
      <label className="row !cursor-pointer items-center !border-0 !py-2">
        <span className="flex-1 text-[15px]">Show message text in notifications</span>
        <input
          type="checkbox"
          className="h-5 w-5 accent-[var(--vk-accent)]"
          checked={!s.hidePreviews}
          onChange={(e) => void save({ ...s, hidePreviews: !e.target.checked })}
        />
      </label>
      <p data-previews-note className="m-0 px-4 pb-2 text-[14px] text-text-3">
        Off by default: notifications say only “Viko · 1 needs you”, so nothing shows on your lock
        screen. On: each device decrypts the ask itself and shows its text, which your lock screen
        can display. Viko stays generic while it is locked.
      </p>
      <div className="flex flex-wrap items-center gap-3 px-4 py-2 text-[15px]">
        <span className="flex-1">Quiet hours</span>
        <input
          aria-label="Quiet hours start"
          type="time"
          className="input !min-h-9 !w-auto !py-1"
          value={s.quietHours.start}
          onChange={(e) =>
            void save({ ...s, quietHours: { ...s.quietHours, start: e.target.value } })
          }
        />
        <span aria-hidden="true">to</span>
        <input
          aria-label="Quiet hours end"
          type="time"
          className="input !min-h-9 !w-auto !py-1"
          value={s.quietHours.end}
          onChange={(e) =>
            void save({ ...s, quietHours: { ...s.quietHours, end: e.target.value } })
          }
        />
      </div>
    </Section>
  )
}
